import { breakpoints } from "@certn/cascadia-tokens";
import { useMemo } from "react";

import { useMediaQuery } from "../../hooks/useMediaQuery";

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const ELLIPSIS = "...";

export function usePaginationRange(
  totalPages: number,
  pageCount: number,
  currentPage: number
) {
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );
  const isMediumDesktop = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-md"].maxWidth.toString()}px)`
  );
  const paginationRange = useMemo(() => {
    if (isMobile || isMediumDesktop) {
      return undefined;
    }
    const NUMBER_ITEMS_ON_LEFT_RIGHT_ELLIPSIS = 2;
    const NUMBER_ITEMS_BESIDE_ELLIPSIS = 6;

    if (totalPages <= NUMBER_ITEMS_ON_LEFT_RIGHT_ELLIPSIS + 5) {
      return range(1, totalPages);
    }
    const leftSiblingIndex = Math.max(
      currentPage - NUMBER_ITEMS_ON_LEFT_RIGHT_ELLIPSIS,
      1
    );
    const rightSiblingIndex = Math.min(
      currentPage + NUMBER_ITEMS_ON_LEFT_RIGHT_ELLIPSIS,
      totalPages
    );
    const shouldShowLeftDots = leftSiblingIndex >= 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;
    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, NUMBER_ITEMS_BESIDE_ELLIPSIS);
      return [...leftRange, ELLIPSIS, totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = range(
        totalPages - NUMBER_ITEMS_BESIDE_ELLIPSIS + 1,
        totalPages
      );
      return [firstPageIndex, ELLIPSIS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [
        firstPageIndex,
        ELLIPSIS,
        ...middleRange,
        ELLIPSIS,
        lastPageIndex,
      ];
    }
  }, [currentPage, totalPages, isMediumDesktop, isMobile]);

  return paginationRange;
}
